<template>
  <div>
    <b-tabs content-class="mt-3" active-nav-item-class="font-weight-bold">
      <b-tab title="Task Details">
        <div class="task-details-container">
          <div class="input-container">
            <b-row>
              <b-col>
                <label class="input-title">Task name</label>
                <b-form-input
                    type="text"
                    :state="informationValidation.isTaskNameProvided"
                    v-model="updatedCollaborativeTaskDetails.taskName"
                    :disabled="!isCollaborativeListAdministrator"></b-form-input>

                <b-form-invalid-feedback v-if="!informationValidation.isTaskNameProvided">
                  This field must not be empty.
                </b-form-invalid-feedback>
              </b-col>
            </b-row>
          </div>
          <div class="input-container">
            <b-row>
              <b-col>
                <label class="input-title">To-do list name</label>
                <b-form-input
                    type="text"
                    v-model="collaborativeTaskDetails.toDoListName"
                    disabled></b-form-input>
              </b-col>
            </b-row>
          </div>
          <div class="input-container">
            <b-row>
              <b-col>
                <label class="input-title">Assignee</label>
                <b-form-select v-model="updatedCollaborativeTaskDetails.assigneeId"
                               :options="assigneeList"
                               :disabled="!isCollaborativeListAdministrator"></b-form-select>
              </b-col>
            </b-row>
          </div>
          <div class="input-container">
            <b-row>
              <b-col>
                <label class="input-title">Priority</label>
                <b-form-select v-model="updatedCollaborativeTaskDetails.priority"
                               :options="priorityOptions"
                               :disabled="!isCollaborativeListAdministrator"></b-form-select>
              </b-col>
            </b-row>
          </div>
          <div class="input-container">
            <b-row>
              <b-col>
                <label class="input-title">Description</label>
                <b-form-input
                    type="text"
                    v-model="updatedCollaborativeTaskDetails.description"
                    :disabled="!isCollaborativeListAdministrator"></b-form-input>
              </b-col>
            </b-row>
          </div>
          <div class="input-container">
            <b-row>
              <b-col>
                <label class="input-title">Due date</label>
                <b-form-datepicker
                    v-model="updatedCollaborativeTaskDetails.dueDate"
                    :disabled="!isCollaborativeListAdministrator"></b-form-datepicker>
                <b-button v-if="updatedCollaborativeTaskDetails.dueDate" variant="outline-danger" @click="removeDueDate"
                          id="removeDueDateButton"
                          :disabled="!isCollaborativeListAdministrator">
                  Remove due date
                </b-button>
              </b-col>
            </b-row>
          </div>
          <div class="input-container">
            <b-row>
              <b-col>
                <label class="input-title">Location</label>
                <b-form-input
                    type="text"
                    v-model="updatedCollaborativeTaskDetails.location"
                    :disabled="!isCollaborativeListAdministrator"></b-form-input>
              </b-col>
            </b-row>
          </div>
          <div class="input-container">
            <b-row>
              <b-col>
                <label class="input-title">Attachment</label>
                <br>
                <input type="file" accept="image/*" @change="onFileChange" id="attachment-input"
                       :disabled="!isCollaborativeListAdministrator"/>
                <b-button v-if="updatedCollaborativeTaskDetails.attachmentUrl" variant="outline-danger"
                          @click="removeAttachment"
                          id="removeAttachmentButton"
                          :disabled="!isCollaborativeListAdministrator">
                  Remove attachment
                </b-button>
                <div id="preview">
                  <img v-if="updatedCollaborativeTaskDetails.attachmentUrl"
                       :src="updatedCollaborativeTaskDetails.attachmentUrl"/>
                </div>
              </b-col>
            </b-row>
          </div>
          <hr>
          <b-button class="main-input-button" block @click="updateCollaborativeTaskDetails"
                    :disabled="!isCollaborativeListAdministrator">Update task details
          </b-button>
        </div>
      </b-tab>
      <b-tab title="Comment">
        <div class="comment-container">
          <comment-cell v-for="comment in commentDocuments" :key="comment.id"
                        :comment-details="comment"></comment-cell>
        </div>
        <div class="comment-input-container">
          <b-row>
            <b-col cols="9" sm="10">
              <b-form-input
                  class="comment-text-field"
                  :state="informationValidation.isCommentContentProvided"
                  type="text"
                  v-model="commentInput"></b-form-input>

              <b-form-invalid-feedback v-if="!informationValidation.isCommentContentProvided">
                This field must not be empty.
              </b-form-invalid-feedback>
            </b-col>
            <b-col>
              <b-button class="send-comment-button secondary-input-button" @click="sendComment">Send</b-button>
            </b-col>
          </b-row>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import CommentCell from "../table_cells/collaborator_cell/CommentCell"
import firebase from "firebase";
import {required} from "vuelidate/lib/validators";
import {db} from "../../main";
import {mapGetters} from "vuex";

export default {
  name: "CollaborativeTaskDetails",
  props: {
    collaborativeTaskDetails: Object
  },
  components: {
    commentCell: CommentCell
  },
  data() {
    return {
      updatedCollaborativeTaskDetails: {
        toDoListId: this.collaborativeTaskDetails.toDoListId,
        taskId: this.collaborativeTaskDetails.taskId,
        taskName: this.collaborativeTaskDetails.taskName,
        toDoListName: this.collaborativeTaskDetails.toDoListName,
        completed: this.collaborativeTaskDetails.completed,
        priority: this.collaborativeTaskDetails.priority,
        description: this.collaborativeTaskDetails.description,
        dueDate: null,
        assigneeId: this.collaborativeTaskDetails.assigneeId,
        assigneeName: this.collaborativeTaskDetails.assigneeName,
        location: this.collaborativeTaskDetails.location,
        taskCreationDate: this.collaborativeTaskDetails.taskCreationDate,
        taskCreationTime: this.collaborativeTaskDetails.taskCreationTime,
        initialAttachmentUrl: this.collaborativeTaskDetails.attachment,
        attachmentUrl: this.collaborativeTaskDetails.attachment,
        attachmentFile: null,
        taskType: 'Collaborative Task'
      },
      commentInput: null,
      commentDocuments: [],

      assigneeList: [
        {value: null, text: 'Unassigned (Not assigned)'}
      ],
      priorityOptions: [
        {value: 0, text: 'High'},
        {value: 1, text: 'Medium'},
        {value: 2, text: 'Low'}
      ],
      informationValidation: {
        isTaskNameProvided: null,
        isCommentContentProvided: null,
      }
    }
  },
  validations: {
    updatedCollaborativeTaskDetails: {
      taskName: {
        required
      },
    },
    commentInput: {
      required
    }
  },
  methods: {
    onFileChange(e) {
      this.updatedCollaborativeTaskDetails.attachmentFile = e.target.files[0];

      if (this.updatedCollaborativeTaskDetails.attachmentFile !== undefined) {
        this.updatedCollaborativeTaskDetails.attachmentUrl = URL.createObjectURL(this.updatedCollaborativeTaskDetails.attachmentFile);
      } else {
        this.updatedCollaborativeTaskDetails.attachmentUrl = null
      }
    },
    removeAttachment() {
      this.updatedCollaborativeTaskDetails.attachmentUrl = null
      document.getElementById("attachment-input").value = "";
    },
    removeDueDate() {
      this.updatedCollaborativeTaskDetails.dueDate = null
    },
    sendComment() {
      // check if the comment is provided
      let isValid = true

      if (!this.$v.commentInput.required) {
        this.informationValidation.isCommentContentProvided = false
        isValid = false
      } else {
        this.informationValidation.isCommentContentProvided = true
      }

      if (isValid) {
        const date = new Date();
        const commentCreationTime = date.getTime()

        const comment = {
          collaboratorEmailAddress: this.$store.getters.user.data.email,
          collaboratorId: this.$store.getters.user.data.id,
          collaboratorName: this.$store.getters.user.data.displayName,
          collaboratorProfilePictureUrl: this.$store.getters.user.data.photoURL,
          commentContent: this.commentInput,
          commentCreationDate: firebase.firestore.FieldValue.serverTimestamp(),
          commentCreationTime: commentCreationTime,
          taskId: this.collaborativeTaskDetails.taskId,
          toDoListId: this.collaborativeTaskDetails.toDoListId,
        }

        db
            .collection('collaborative_to_do_lists/' + this.collaborativeTaskDetails.toDoListId + '/tasks/' + this.collaborativeTaskDetails.taskId + '/comments')
            .add(comment)
            .then(docRef => {
              this.commentInput = null
            })
      }
    },
    assigneeNameComparatorFunction(a, b) {
      const collaboratorAName = a.text
      const collaboratorBName = b.text

      let comparison = 0
      if (collaboratorAName > collaboratorBName) {
        comparison = 1
      } else if (collaboratorAName < collaboratorBName) {
        comparison = -1
      }

      return comparison
    },
    updateCollaborativeTaskDetails() {
      // check if all information is valid
      let isValid = true

      if (!this.$v.updatedCollaborativeTaskDetails.taskName.required) {
        this.informationValidation.isTaskNameProvided = false
        isValid = false
      } else {
        this.informationValidation.isTaskNameProvided = true
      }

      if (isValid) {
        // retrieve the assignee name to be added in the updatedCollaborativeTaskDetails
        const retrievedAssigneeName = this.assigneeList.filter(obj => {
          return obj.value === this.updatedCollaborativeTaskDetails.assigneeId
        })
        this.updatedCollaborativeTaskDetails.assigneeName = retrievedAssigneeName[0].text !== 'Unassigned (Not assigned)' ? retrievedAssigneeName[0].text : null

        this.$store.dispatch('updateCollaborativeTask', this.updatedCollaborativeTaskDetails)
        this.hideModal('task-details-modal')
      }
    },
    showModal(id) {
      this.$root.$emit('bv::show::modal', id)
    },
    hideModal(id) {
      this.$root.$emit('bv::hide::modal', id)
    }
  },
  computed: {
    ...mapGetters([
      "homeSelectedComponent",
      "isCollaborativeListAdministrator"
    ])
  },
  mounted() {
    // loading for retrieving task details on Assigned list category only
    if (this.homeSelectedComponent !== 'collaborativeTaskList') {
      this.showModal('loading-modal')
    }

    // retrieve the list collaborator
    db
        .collectionGroup('user_collaborative_to_do_list_participation')
        .where('collaborativeToDoListId', '==', this.updatedCollaborativeTaskDetails.toDoListId)
        .onSnapshot(querySnapshot => {
          const userCollaborativeToDoListParticipationObject = querySnapshot.docs.map(doc => doc.data())

          let tempAssigneeList = []

          userCollaborativeToDoListParticipationObject.forEach(collaborator => {
            let assignee = {
              value: collaborator.collaboratorId,
              text: collaborator.collaboratorName
            }
            tempAssigneeList.push(assignee)
          })

          // sort the assignee list alphabetically before adding the collaborator to the assignee list
          let sortedAssigneeList = tempAssigneeList.sort(this.assigneeNameComparatorFunction)

          sortedAssigneeList.forEach(assignee => {
            this.assigneeList.push(assignee)
          })
        })

    // retrieve the comments
    this.$bind(
        'commentDocuments',
        db
            .collection('collaborative_to_do_lists/' + this.collaborativeTaskDetails.toDoListId + '/tasks/' + this.collaborativeTaskDetails.taskId + '/comments')
            .where('taskId', '==', this.collaborativeTaskDetails.taskId)
            .orderBy('commentCreationTime', 'asc')
    )

    // retrieve more data if accessing the task from the Assigned category
    if (this.homeSelectedComponent !== 'collaborativeTaskList') {
      db
          .collection('users/' + this.collaborativeTaskDetails.assigneeId + '/user_collaborative_to_do_list_participation')
          .where('collaborativeToDoListId', '==', this.collaborativeTaskDetails.toDoListId)
          .get()
          .then(querySnapshot => {
            querySnapshot.docs.map(doc => {
              this.$store.dispatch('setIsCollaborativeListAdministrator', doc.data().collaborativeToDoListRole === 'Administrator')
              this.hideModal('loading-modal')
            })
          })
    }

    // convert the format of the task due date
    if (this.collaborativeTaskDetails.dueDate !== null) {
      const taskDueDate = new Date(this.collaborativeTaskDetails.dueTime)
      const year = taskDueDate.getFullYear();
      const month = taskDueDate.getMonth() + 1 < 10 ? "0" + (taskDueDate.getMonth() + 1) : taskDueDate.getMonth();
      const day = taskDueDate.getDate() < 10 ? "0" + taskDueDate.getDate() : taskDueDate.getDate();

      this.updatedCollaborativeTaskDetails.dueDate = year + "-" + month + "-" + day
    }

    // check if the task has initial image
    this.updatedCollaborativeTaskDetails.haveInitialAttachment = this.updatedCollaborativeTaskDetails.attachmentUrl !== null;
  },
  destroyed() {
    // reset isCollaborativeListAdministrator each time the user leave the list details in the Assigned category
    if (this.homeSelectedComponent === 'assigned') {
      this.$store.dispatch('setIsCollaborativeListAdministrator', null)
    }
  }
}
</script>

<style scoped>
#removeDueDateButton {
  margin-top: 1em;
}

#removeAttachmentButton {
  margin-top: 1em;
  margin-bottom: 1em;
}

#preview {
  display: flex;
  justify-content: center;
  align-items: center;
}

#preview img {
  max-width: 100%;
  max-height: 500px;
}

.comment-container {
  height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.comment-input-container {
  margin-top: 1em;
}

.input-container {
  margin-bottom: 1em;
}

.input-title {
  font-weight: bold;
}

/*.comment-text-field {*/
/*  float: left;*/
/*  width: 84%;*/
/*}*/

/*.send-comment-button {*/
/*  float: right;*/
/*  margin-left: 1em;*/
/*}*/

.secondary-input-button {
  background-color: white;
  color: #212441;
  padding: 0.5em;
}

.secondary-input-button:hover {
  background-color: #212441;
  color: white;
  padding: 0.5em;
}

.main-input-button {
  font-weight: bold;
  background-color: #212441;
  padding: 0.5em;
}

.main-input-button:hover {
  font-weight: bold;
  background-color: #FFFFFF;
  color: #212441;
  padding: 0.5em;
}
</style>
